import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Box,
  DialogContentText,
  TextField,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DevWinBadge } from '../../components/DevPageBadge';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ProjectForm } from '../../components/Forms';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';

// code ของ system role (hardcode ใน backend/constants/systemRoles.constant.js) — เทียบ code ไม่ใช่ชื่อ
// เพราะชื่อ role จริงคือ "engineer/production" ซึ่งเทียบชื่อแล้วไม่ตรง ENGINEER/PRODUCTION ตัวใดตัวหนึ่ง
const PM_ROLE_CODES = ['ENGINEER'];

export default function ProjectEditPopup({ propId, open, onClose, onSaved, title }) {
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project);
  const contact = useSelector((state) => state.contact);

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  const [date, setDate] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [includeVAT, setIncludeVAT] = useState(false);
  const [cost, setCost] = useState('');
  const [extraGpsSites, setExtraGpsSites] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);

  // ตอนเพิ่มโครงการใหม่ dropdown "Project Manager" แสดงเฉพาะพนักงานที่มี system role
  // engineer/production — โหมดแก้ไขยังแสดงทั้งหมด (กัน PM เดิมที่ไม่มี role นี้หายจาก Select)
  const pmEmployees = propId
    ? allEmployees
    : allEmployees.filter((emp) => (emp?.system_role || []).some((r) => PM_ROLE_CODES.includes(r?.code)));

  useEffect(() => {
    if (!open) return;
    dispatch(actions.contactAll({}));

    const fetchAllEmployees = async () => {
      const PAGE_SIZE = 50;
      let pg = 1;
      let accumulated = [];
      let hasMore = true;
      while (hasMore) {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/employee?name=&size=${PAGE_SIZE}&page=${pg}&activeOnly=false`,
        );
        const rows = data?.rows || [];
        const total = data?.total || 0;
        accumulated = [...accumulated, ...rows];
        setAllEmployees([...accumulated]);
        hasMore = accumulated.length < total && rows.length > 0;
        pg++;
      }
    };
    fetchAllEmployees();
    if (!propId) {
      reset();
      setDate(null);
      setDateEnd(null);
      setIncludeVAT(false);
      setCost('');
      setExtraGpsSites([]);
      setDocuments([]);
      setSelectedClient(null);
      return;
    }
    dispatch(actions.projectGet(propId));
  }, [open, propId]);

  useEffect(() => {
    if (!propId || !project?.name || project?._id !== propId) return;
    setValue('engineer', project?.engineer?._id);
    setValue('name', project?.name);
    setValue('project_number', project?.project_number);
    setValue('description', project?.description);
    setValue('customer', project?.customer);
    setValue('client_id', project?.client_id?._id || project?.client_id);
    setValue('place', project?.place);
    setValue('google_drive_url', project?.google_drive_url);
    setValue('labur_cost', project?.labur_cost);
    setValue('operation_status', project?.operation_status || 'PENDING');
    setValue('gps.lat', project?.gps?.lat);
    setValue('gps.lon', project?.gps?.lon);
    setCost(project?.cost);
    setDate(project?.date_start);
    setDateEnd(project?.date_end);
    setIncludeVAT(project?.include_vat || false);
    
    // Set selected client
    if (project?.client_id) {
      const clientId = typeof project.client_id === 'object' ? project.client_id._id : project.client_id;
      const foundClient = contact?.rows?.find((c) => c._id === clientId);
      if (foundClient) {
        setSelectedClient(foundClient);
      }
    }
    
    const sites = project?.gps_sites;
    setExtraGpsSites(
      Array.isArray(sites) && sites.length
        ? sites.map((s) => ({ name: s.name || '', lat: s.lat != null ? String(s.lat) : '', lon: s.lon != null ? String(s.lon) : '' }))
        : [],
    );
    const docs = project?.documents;
    setDocuments(Array.isArray(docs) && docs.length ? docs.map((d) => ({ title: d.title || '', url: d.url || '' })) : []);
  }, [project?.name, project?._id, contact?.rows]);

  const handleRequestClose = () => {
    if (isDirty) {
      setConfirmClose(true);
    } else {
      handleForceClose();
    }
  };

  const handleForceClose = () => {
    setConfirmClose(false);
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const gpsSitesPayload = extraGpsSites
        .map((s) => ({ name: (s.name || '').trim(), lat: parseFloat(s.lat), lon: parseFloat(s.lon) }))
        .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon));

      const latRaw = data?.gps?.lat ?? data['gps.lat'];
      const lonRaw = data?.gps?.lon ?? data['gps.lon'];
      const lat = latRaw !== '' && latRaw != null ? parseFloat(latRaw) : NaN;
      const lon = lonRaw !== '' && lonRaw != null ? parseFloat(lonRaw) : NaN;

      const payload = {
        ...data,
        date_start: date,
        date_end: dateEnd,
        cost,
        vat: includeVAT ? 0 : cost * 0.07,
        include_vat: includeVAT,
        gps: Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : undefined,
        gps_sites: gpsSitesPayload,
        documents: documents.filter((d) => d.url.trim()),
      };

      if (propId) {
        await dispatch(actions.projectPut(propId, payload));
      } else {
        await dispatch(actions.projectCreate(payload));
      }
      reset({}, { keepValues: true });
      onSaved?.();
      onClose();
    } catch (err) {
      showAlert('บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DevWinBadge num={112} name="Project · Edit/New Popup" open={open} />
      <Dialog
        open={open}
        onClose={handleRequestClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{title || 'แก้ไขโครงการ'}</Typography>
              {propId && <Typography variant="caption" color="text.secondary">{project?.project_number}</Typography>}
            </Box>
            <IconButton size="small" onClick={handleRequestClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {(propId && project?.isLoading) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form id="edit-project-form" onSubmit={handleSubmit(onSubmit)}>
              <ProjectForm
                Controller={Controller}
                control={control}
                date={date}
                setDate={setDate}
                dateEnd={dateEnd}
                setDateEnd={setDateEnd}
                employee={pmEmployees}
                contacts={contact?.rows || []}
                includeVAT={includeVAT}
                setIncludeVAT={setIncludeVAT}
                cost={cost}
                setCost={setCost}
                extraGpsSites={extraGpsSites}
                setExtraGpsSites={setExtraGpsSites}
                selectedClient={selectedClient}
                setSelectedClient={setSelectedClient}
                setValue={setValue}
              />

              <Divider sx={{ my: 2 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" fontWeight={600}>เอกสารแนบ (Google Drive)</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setDocuments((prev) => [...prev, { title: '', url: '' }])}
                >
                  เพิ่มลิงค์
                </Button>
              </Stack>
              <Stack spacing={1}>
                {documents.map((doc, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      placeholder="ชื่อเอกสาร"
                      value={doc.title}
                      onChange={(e) => setDocuments((prev) => prev.map((d, idx) => idx === i ? { ...d, title: e.target.value } : d))}
                      sx={{ width: 180, flexShrink: 0 }}
                    />
                    <TextField
                      size="small"
                      placeholder="URL (Google Drive)"
                      value={doc.url}
                      onChange={(e) => setDocuments((prev) => prev.map((d, idx) => idx === i ? { ...d, url: e.target.value } : d))}
                      fullWidth
                    />
                    <IconButton
                      size="small"
                      disabled={!doc.url.trim()}
                      onClick={() => window.open(doc.url, '_blank')}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setDocuments((prev) => prev.filter((_, idx) => idx !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                {documents.length === 0 && (
                  <Typography variant="caption" color="text.secondary">ยังไม่มีเอกสาร</Typography>
                )}
              </Stack>
            </form>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button variant="outlined" size="small" onClick={handleRequestClose}>ยกเลิก</Button>
          <Button
            variant="contained"
            size="small"
            form="edit-project-form"
            type="submit"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {saving ? 'กำลังบันทึก...' : propId ? 'บันทึก' : 'สร้างโครงการ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm discard */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)} maxWidth="xs">
        <DialogTitle>มีการแก้ไขที่ยังไม่บันทึก</DialogTitle>
        <DialogContent>
          <DialogContentText>ต้องการบันทึกหรือละทิ้งการแก้ไข?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmClose(false)}>กลับไปแก้ไข</Button>
          <Button size="small" color="error" onClick={handleForceClose}>ละทิ้ง</Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => { setConfirmClose(false); handleSubmit(onSubmit)(); }}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
